'use client';

import { useEffect, useRef, useState } from 'react';
import { animate, useMotionValue, type AnimationPlaybackControls } from 'framer-motion';
import { cn } from '@/lib/utils';

const clamp = (n: number) => Math.max(0, Math.min(100, n));

function scoreColor(v: number): string {
  return v >= 67 ? 'var(--success-green)' : v >= 34 ? 'var(--status-warning)' : 'var(--alert-red)';
}

interface Stage {
  min: number;
  label: string;
  desc: string;
}

/**
 * Staged explanations of what the current score means — the tooltip picks the
 * highest stage whose `min` the score has reached, so the message sharpens as the
 * campaign climbs.
 */
const STAGES: Stage[] = [
  { min: 0, label: 'Not started', desc: 'Your campaign score is a live measure of how consistently you act. Send your first applications and intros to get it moving.' },
  { min: 8, label: 'Flickering', desc: 'A first spark of activity. Do a little every day — momentum compounds quickly from here.' },
  { min: 18, label: 'Warming up', desc: 'You’re starting a habit. Keep a steady daily pace to climb out of the dormant zone.' },
  { min: 30, label: 'Warming', desc: 'Real momentum is forming. Consistency now is what turns a warm campaign into an active one.' },
  { min: 42, label: 'Steady', desc: 'You’re showing up regularly. Nudge your daily volume up a little to reach the Active band.' },
  { min: 52, label: 'Active', desc: 'You’re consistently running your plan — this is where results start to show up.' },
  { min: 62, label: 'Humming', desc: 'Strong, reliable execution. This is the cadence that gets candidates noticed.' },
  { min: 72, label: 'Thriving', desc: 'You’re outpacing most job seekers. Protect your streak and keep the pressure on.' },
  { min: 82, label: 'On fire', desc: 'Peak momentum — you’re doing the work that lands interviews. Don’t let up.' },
  { min: 92, label: 'Unstoppable', desc: 'Elite pace. Very few sustain this — you’re making the most of every single day.' },
  { min: 100, label: 'Maxed out', desc: 'A perfect score: you’re executing your entire plan, every day. Outstanding.' },
];

function stageFor(score: number): Stage {
  for (let i = STAGES.length - 1; i >= 0; i -= 1) {
    if (score >= STAGES[i].min) return STAGES[i];
  }
  return STAGES[0];
}

/**
 * StrategyScoreRing — a 0–100 progress ring, adapted from the automations
 * RailMeter SVG gauge. Colour warms red → amber → green as the score climbs.
 *
 * When `interactive`, hovering (or tapping) the ring replays a fill from 0 up to
 * the current score and shows a staged tooltip explaining what that score means.
 */
export function StrategyScoreRing({
  score,
  size = 90,
  stroke = 9,
  interactive = false,
}: {
  score: number;
  size?: number;
  stroke?: number;
  interactive?: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const controls = useRef<AnimationPlaybackControls | null>(null);
  const [hovering, setHovering] = useState(false);
  const mv = useMotionValue(score);
  // Animated value used only while hovering; at rest we render the real score.
  const [animated, setAnimated] = useState(score);

  // Dismiss a tap-opened tooltip on outside pointer (touch has no pointerleave).
  useEffect(() => {
    if (!hovering) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setHovering(false);
        controls.current?.stop();
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [hovering]);

  const start = () => {
    if (!interactive || hovering) return;
    setHovering(true);
    controls.current?.stop();
    mv.set(0);
    setAnimated(0);
    controls.current = animate(mv, score, {
      duration: 0.85,
      ease: [0.4, 0, 0.2, 1],
      onUpdate: setAnimated,
    });
  };
  const stop = () => {
    if (!hovering) return;
    setHovering(false);
    controls.current?.stop();
  };

  const display = hovering ? animated : score;
  const stage = stageFor(score);
  const color = scoreColor(display);
  const clamped = clamp(display) / 100;

  const r = (size - stroke) / 2;
  const c = size / 2;
  const len = 2 * Math.PI * r;

  return (
    <div
      ref={wrapRef}
      className={cn('relative inline-grid place-items-center', interactive && 'cursor-pointer')}
      style={{ width: size, height: size }}
      onPointerEnter={interactive ? start : undefined}
      onPointerDown={interactive ? start : undefined}
      onPointerLeave={interactive ? stop : undefined}
    >
      {interactive && hovering && (
        <div
          className="pointer-events-none absolute z-20 w-56 rounded-lg border border-border bg-popover p-2.5 text-popover-foreground"
          style={{ left: 0, top: 'calc(100% + 10px)', boxShadow: 'var(--shadow-warm-md)' }}
        >
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: scoreColor(score) }} />
            <span className="text-label font-bold text-foreground">{stage.label}</span>
            <span className="ml-auto text-2xs font-semibold tabular-nums text-muted-foreground">
              {Math.round(score)} / 100
            </span>
          </div>
          <p className="mt-1 text-caption leading-snug text-muted-foreground">{stage.desc}</p>
        </div>
      )}

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90 text-muted-foreground/25"
        role="img"
        aria-label={`Strategy score ${Math.round(score)} of 100`}
      >
        <circle cx={c} cy={c} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={len}
          strokeDashoffset={len * (1 - clamped)}
          style={{ transition: 'stroke 200ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center">
        <div
          className="font-extrabold tabular-nums text-foreground"
          style={{ fontSize: size * 0.28, lineHeight: 1 }}
        >
          {Math.round(display)}
        </div>
        <div className="text-overline text-muted-foreground" style={{ marginTop: 2 }}>
          score
        </div>
      </div>
    </div>
  );
}
